// SettingsModal.jsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Context
import { useUser } from 'context/UserContext';
import { ThemeMode, useTheme } from 'context/ThemeContext';

// Hooks
import { useIsMobile } from 'hooks/useDeviceDetect';
import useWorkspaceAccess from 'hooks/useWorkspaceAccess';
import useSettingsMembers from './hooks/useSettingsMembers';

// Services
import { deleteNode } from 'services/nodes/NodeWriteService';
import { deleteWorkspaceByKey } from 'services/workspaces/WorkspaceWriteService';

// CSS
import './SettingsModal.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Confirm from 'components/confirm/Confirm';
import Modal from 'components/modal/Modal';
import SettingsCoverPane from './components/SettingsCoverPane';
import SettingsIconPane from './components/SettingsIconPane';
import SettingsAccountDisplayPane from './components/SettingsAccountDisplayPane';
import SettingsDisplayPane from './components/SettingsDisplayPane';
import SettingsIntegrationsPane from './components/SettingsIntegrationsPane';
import SettingsLoginPane from './components/SettingsLoginPane';
import SettingsMembersPane from './components/SettingsMembersPane';
import SettingsModalButton from './button/SettingsModalButton';
import SettingsChecklistsPane from './components/SettingsChecklistsPane';
import SettingsNamePane from './components/SettingsNamePane';
import SettingsPathPane from './components/SettingsPathPane';
import SettingsPlanPane from './components/SettingsPlanPane';
import SettingsProfilesPane from './components/SettingsProfilesPane';
import SettingsProfilePhotoPane from './components/SettingsProfilePhotoPane';
import SettingsUserNamePane from './components/SettingsUserNamePane';

const TAB_NAME = 'name';
const TAB_PATH = 'path';
const TAB_PLAN = 'plan';
const TAB_MEMBERS = 'members';
const TAB_PROFILES = 'profiles';
const TAB_ICON = 'icon';
const TAB_COVER = 'cover';
const TAB_INTEGRATIONS = 'integrations';
const TAB_CHECKLISTS = 'checklists';
const TAB_PROFILE_PHOTO = 'profile-photo';
const TAB_USER_NAME = 'user-name';
const TAB_DEFAULT_DISPLAY = 'theme-default';
const TAB_ACCOUNT_DISPLAY = 'theme-account';
const TAB_LOGIN = 'login';

const TAB_LABELS = {
    [TAB_NAME]: 'Name',
    [TAB_PATH]: 'Path',
    [TAB_PLAN]: 'Plan',
    [TAB_MEMBERS]: 'Members',
    [TAB_PROFILES]: 'Profiles',
    [TAB_ICON]: 'Icon',
    [TAB_COVER]: 'Cover',
    [TAB_INTEGRATIONS]: 'Integrations',
    [TAB_CHECKLISTS]: 'Checklists',
    [TAB_PROFILE_PHOTO]: 'Profile Photo',
    [TAB_USER_NAME]: 'User Name',
    [TAB_DEFAULT_DISPLAY]: 'Default Display',
    [TAB_ACCOUNT_DISPLAY]: 'Display',
    [TAB_LOGIN]: 'Login',
};

const ROLE_OWNER = 'owner';
const ROLE_ADMIN = 'admin';

function resolveInitialSettingsTab(requestedTab, canSeeWorkspaceSettings) {
    const normalizedTab = String(requestedTab || '').trim();

    if (
        normalizedTab === TAB_ACCOUNT_DISPLAY
        || normalizedTab === TAB_PROFILE_PHOTO
        || normalizedTab === TAB_USER_NAME
        || normalizedTab === TAB_LOGIN
    ) {
        return normalizedTab;
    }

    if (canSeeWorkspaceSettings && normalizedTab) {
        return normalizedTab;
    }

    if (canSeeWorkspaceSettings) {
        return TAB_MEMBERS;
    }

    return TAB_ACCOUNT_DISPLAY;
}

/**
 * SettingsModal
 *
 * Renders workspace and account settings panels for the switcher popup.
 *
 * @param {boolean} isOpen - Whether the modal is currently open.
 * @param {function} setOpen - Setter for modal visibility.
 * @param {string|null} initialTab - Optional tab key to activate when the modal opens.
 * @param {object} workspace - Active workspace model.
 * @param {function} onInviteClick - Callback for opening member invite flow.
 * @param {function} onClose - Callback fired after modal close.
 *
 * @returns {ReactNode} The rendered settings modal.
 */
const SettingsModal = React.memo(({
    isOpen,
    setOpen,
    initialTab = null,
    workspace,
    onInviteClick,
    onClose,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const {
        theme,
        themeMode,
        setWorkspaceDefaultThemeMode,
        setThemeModeOverride,
        isUsingWorkspaceThemeDefault,
        workspaceDefaultThemeMode,
    } = useTheme();

    // User Context
    const { user, setUser } = useUser();

    // ----- HOOKS ----- //

    // Router navigation helper.
    const navigate = useNavigate();

    // Device breakpoint detection.
    const isMobile = useIsMobile();

    // Workspace permission helpers.
    const { workspaceRole, isRoleLoading, can } = useWorkspaceAccess(workspace);

    // ----- STATE ----- //

    // Active Tab State
    const [activeTab, setActiveTab] = useState(TAB_MEMBERS);

    // Delete-account confirm modal state.
    const [deleteAccountConfirmOpen, setDeleteAccountConfirmOpen] = useState(false);

    // Delete-account in-flight state.
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);

    // Delete-workspace confirm modal state.
    const [deleteWorkspaceConfirmOpen, setDeleteWorkspaceConfirmOpen] = useState(false);

    // Delete-workspace in-flight state.
    const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);

    // Mobile dropdown menu open state.
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // ----- MEMOS ----- //

    // Members role-change permission.
    const canChangeRoles = can('members.changeRole');

    // Members removal permission.
    const canDeleteMembers = can('members.remove');

    // Invite creation permission.
    const canInviteMembers = can('invite.create');

    // Workspace edit permission.
    const canEditWorkspace = can('workspace.update');

    // Workspace delete permission.
    const canDeleteWorkspace = can('workspace.delete') && workspace?.isDefault !== true;

    // Owner/admin-only workspace settings gate.
    const canSeeWorkspaceSettings = workspaceRole === ROLE_OWNER || workspaceRole === ROLE_ADMIN;

    // Effective dark mode state.
    const isDarkMode = themeMode === ThemeMode.DARK;

    // Workspace default dark mode state.
    const isWorkspaceDefaultDarkMode = workspaceDefaultThemeMode === ThemeMode.DARK;

    // Active tab display label.
    const activeTabLabel = TAB_LABELS[activeTab] || 'Settings';

    // Members pane state and actions.
    const {
        sortedMembers,
        subtitle,
        errorMessage,
        statusMessage,
        clearMessages,
        canManageTarget,
        handleRoleChange,
        handleRemoveMember,
    } = useSettingsMembers({
        isOpen: isOpen && canSeeWorkspaceSettings,
        workspaceKey: workspace?.key,
        workspaceRole,
        userKey: user?.key,
        canChangeRoles,
        canDeleteMembers,
    });

    useEffect(() => {
        if (!isOpen || isRoleLoading) return;

        setActiveTab(resolveInitialSettingsTab(initialTab, canSeeWorkspaceSettings));
    }, [canSeeWorkspaceSettings, initialTab, isOpen, isRoleLoading]);

    useEffect(() => {
        if (isRoleLoading || canSeeWorkspaceSettings) return;

        if (
            activeTab === TAB_NAME
            || activeTab === TAB_PATH
            || activeTab === TAB_PLAN
            || activeTab === TAB_MEMBERS
            || activeTab === TAB_PROFILES
            || activeTab === TAB_DEFAULT_DISPLAY
            || activeTab === TAB_ICON
            || activeTab === TAB_COVER
            || activeTab === TAB_INTEGRATIONS
            || activeTab === TAB_CHECKLISTS
        ) {
            setActiveTab(TAB_ACCOUNT_DISPLAY);
        }
    }, [activeTab, canSeeWorkspaceSettings, isRoleLoading]);

    useEffect(() => {
        if (isMobile && isOpen) return;

        setIsMobileMenuOpen(false);
    }, [isMobile, isOpen]);

    // ----- HANDLERS ----- //

    /**
     * Closes settings and clears member pane status state.
     */
    const handleClose = () => {
        clearMessages();
        setDeleteAccountConfirmOpen(false);
        setDeleteWorkspaceConfirmOpen(false);
        setIsMobileMenuOpen(false);
        setOpen && setOpen(false);
        onClose && onClose();
    };

    /**
     * Sets the active settings tab and dismisses the mobile menu.
     *
     * @param {string} nextTab - The tab key to activate.
     */
    const handleTabChange = (nextTab) => {
        clearMessages();
        setActiveTab(nextTab);
        setIsMobileMenuOpen(false);
    };

    /**
     * Activates the name tab.
     */
    const handleNameTabClick = () => {
        handleTabChange(TAB_NAME);
    };

    /**
     * Activates the path tab.
     */
    const handlePathTabClick = () => {
        handleTabChange(TAB_PATH);
    };

    /**
     * Activates the plan tab.
     */
    const handlePlanTabClick = () => {
        handleTabChange(TAB_PLAN);
    };

    /**
     * Activates the members tab.
     */
    const handleMembersTabClick = () => {
        handleTabChange(TAB_MEMBERS);
    };

    /**
     * Activates the profiles tab.
     */
    const handleProfilesTabClick = () => {
        handleTabChange(TAB_PROFILES);
    };

    /**
     * Activates the display tab.
     */
    const handleDefaultDisplayTabClick = () => {
        handleTabChange(TAB_DEFAULT_DISPLAY);
    };

    /**
     * Activates the account display tab.
     */
    const handleAccountDisplayTabClick = () => {
        handleTabChange(TAB_ACCOUNT_DISPLAY);
    };

    /**
     * Activates the workspace cover tab.
     */
    const handleIconTabClick = () => {
        handleTabChange(TAB_ICON);
    };

    /**
     * Activates the workspace cover tab.
     */
    const handleCoverTabClick = () => {
        handleTabChange(TAB_COVER);
    };

    /**
     * Activates the workspace integrations tab.
     */
    const handleIntegrationsTabClick = () => {
        handleTabChange(TAB_INTEGRATIONS);
    };

    /**
     * Activates the workspace checklists tab.
     */
    const handleChecklistsTabClick = () => {
        handleTabChange(TAB_CHECKLISTS);
    };

    /**
     * Activates the user name tab.
     */
    const handleUserNameTabClick = () => {
        handleTabChange(TAB_USER_NAME);
    };

    /**
     * Activates the profile photo tab.
     */
    const handleProfilePhotoTabClick = () => {
        handleTabChange(TAB_PROFILE_PHOTO);
    };

    /**
     * Toggles workspace theme mode.
     *
     * @param {boolean} nextEnabled - Whether dark mode should be enabled.
     */
    const handleWorkspaceDefaultThemeModeChange = (nextEnabled) => {
        setWorkspaceDefaultThemeMode(nextEnabled ? ThemeMode.DARK : ThemeMode.LIGHT);
    };

    /**
     * Sets member-level theme mode override.
     *
     * @param {boolean} nextEnabled - Whether dark mode should be enabled.
     */
    const handleAccountThemeModeChange = (nextEnabled) => {
        setThemeModeOverride(nextEnabled ? ThemeMode.DARK : ThemeMode.LIGHT);
    };

    /**
     * Activates the login tab.
     */
    const handleLoginTabClick = () => {
        handleTabChange(TAB_LOGIN);
    };

    /**
     * Opens the delete account confirmation modal.
     */
    const handleDeleteAccountClick = () => {
        clearMessages();
        setIsMobileMenuOpen(false);
        setDeleteAccountConfirmOpen(true);
    };

    /**
     * Opens the delete workspace confirmation modal.
     */
    const handleDeleteWorkspaceClick = () => {
        clearMessages();
        setIsMobileMenuOpen(false);
        setDeleteWorkspaceConfirmOpen(true);
    };

    /**
     * Toggles the mobile settings menu dropdown.
     */
    const handleMobileMenuToggle = () => {
        setIsMobileMenuOpen((prev) => !prev);
    };

    const navigationContents = (
        <>
            {canSeeWorkspaceSettings && (
                <>
                    <div
                        className="settings-modal-nav-group-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        SPACE
                    </div>

                    <SettingsModalButton
                        iconKey="edit"
                        label="Name"
                        onClick={handleNameTabClick}
                    />

                    <SettingsModalButton
                        iconKey="link"
                        label="Path"
                        onClick={handlePathTabClick}
                    />

                    <SettingsModalButton
                        iconKey="image"
                        label="Icon"
                        onClick={handleIconTabClick}
                    />

                    <SettingsModalButton
                        iconKey="image"
                        label="Cover"
                        onClick={handleCoverTabClick}
                    />

                    <SettingsModalButton
                        iconKey="paid"
                        label="Plan"
                        onClick={handlePlanTabClick}
                    />

                    <SettingsModalButton
                        iconKey="group"
                        label="Members"
                        onClick={handleMembersTabClick}
                    />

                    <SettingsModalButton
                        iconKey="badge"
                        label="Profiles Setup"
                        onClick={handleProfilesTabClick}
                    />

                    <SettingsModalButton
                        iconKey="display_settings"
                        label="Default Display"
                        onClick={handleDefaultDisplayTabClick}
                    />

                    <SettingsModalButton
                        iconKey="api"
                        label="Integrations"
                        onClick={handleIntegrationsTabClick}
                    />

                    <SettingsModalButton
                        iconKey="checklist"
                        label="Checklists"
                        onClick={handleChecklistsTabClick}
                    />

                    {canDeleteWorkspace && (
                        <SettingsModalButton
                            iconKey="delete"
                            label="Delete Space"
                            onClick={handleDeleteWorkspaceClick}
                        />
                    )}
                </>
            )}

            <div
                className="settings-modal-nav-group-label settings-modal-nav-group-label-account"
                style={{
                    color: theme.foregroundMuted,
                }}
            >
                MY ACCOUNT
            </div>

            <SettingsModalButton
                iconKey="person"
                label="My Name"
                onClick={handleUserNameTabClick}
            />

            <SettingsModalButton
                iconKey="account_circle"
                label="Profile Photo"
                onClick={handleProfilePhotoTabClick}
            />

            <SettingsModalButton
                iconKey="display_settings"
                label="Display"
                onClick={handleAccountDisplayTabClick}
            />

            <SettingsModalButton
                iconKey="lock"
                label="Login"
                onClick={handleLoginTabClick}
            />

            <SettingsModalButton
                iconKey="delete"
                label="Delete Account"
                onClick={handleDeleteAccountClick}
            />
        </>
    );

    /**
     * Deletes the current user node and routes back to root.
     */
    const handleConfirmDeleteAccount = async () => {
        if (isDeletingAccount) return;

        const userKey = String(user?.key || '').trim();
        if (!userKey) {
            setDeleteAccountConfirmOpen(false);
            return;
        }

        setIsDeletingAccount(true);

        try {
            await deleteNode(userKey);
            setDeleteAccountConfirmOpen(false);
            setUser && setUser(null);
            handleClose();
            navigate('/', { replace: true });
        } catch (error) {
            console.error('[SettingsModal] Failed to delete account:', error);
            window.alert('Unable to delete account right now. Please try again.');
        } finally {
            setIsDeletingAccount(false);
        }
    };

    /**
     * Queues deletion for the current non-default workspace.
     */
    const handleConfirmDeleteWorkspace = async () => {
        if (isDeletingWorkspace) return;

        const workspaceKey = String(workspace?.key || '').trim();
        if (!workspaceKey) {
            setDeleteWorkspaceConfirmOpen(false);
            return;
        }

        setIsDeletingWorkspace(true);

        try {
            await deleteWorkspaceByKey(workspaceKey);
            setDeleteWorkspaceConfirmOpen(false);
            handleClose();
            navigate('/', { replace: true });
            window.alert('Workspace deletion queued. If this workspace remains selected briefly, refresh after the delete finishes.');
        } catch (error) {
            console.error('[SettingsModal] Failed to delete workspace:', error);
            window.alert(error?.message || 'Unable to delete this workspace right now. Please try again.');
        } finally {
            setIsDeletingWorkspace(false);
        }
    };

    // const handlePayoutsTabClick = () => {
    //     setErrorMessage('');
    //     setStatusMessage('');
    //     setActiveTab(TAB_PAYOUTS);
    // };

    // ----- RENDER ----- //

    return (
        <>
            <Modal
                iconKey="settings"
                title="Settings"
                isOpen={isOpen}
                onClose={handleClose}
                height={isMobile ? '100%' : '760px'}
                width={isMobile ? '100%' : '920px'}
            >
                <div className="settings-modal-container">

                    {/* LEFT NAV */}
                    {!isMobile && (
                        <div
                            className="settings-modal-nav"
                            style={{
                                borderRightColor: theme.backgroundHover,
                            }}
                        >
                            {navigationContents}
                        </div>
                    )}

                    {/* RIGHT PANE */}
                    <div className="settings-modal-pane"
                        style={{
                            margin: isMobile ? '12px' : '0px',
                        }}
                    >
                        {isMobile && (
                            <div className="settings-modal-mobile-menu">
                                <button
                                    type="button"
                                    className="settings-modal-mobile-menu-button"
                                    style={{
                                        backgroundColor: isMobileMenuOpen ? theme.backgroundHover : theme.backgroundMuted,
                                        borderColor: theme.backgroundHover,
                                    }}
                                    aria-expanded={isMobileMenuOpen}
                                    aria-label="Open settings menu"
                                    onClick={handleMobileMenuToggle}
                                >
                                    <div className="settings-modal-mobile-menu-button-main">
                                        <div className="settings-modal-mobile-menu-button-icon">
                                            <Icon
                                                iconKey="menu"
                                                width={18}
                                                height={18}
                                                color={theme.foregroundMuted}
                                            />
                                        </div>

                                        <div
                                            className="settings-modal-mobile-menu-button-title"
                                            style={{
                                                color: theme.foreground,
                                            }}
                                        >
                                            Menu
                                        </div>
                                    </div>

                                    <div className="settings-modal-mobile-menu-button-main">
                                        <div
                                            className="settings-modal-mobile-menu-button-value"
                                            style={{
                                                color: theme.foregroundMuted,
                                            }}
                                        >
                                            {activeTabLabel}
                                        </div>

                                        <div className="settings-modal-mobile-menu-button-icon">
                                            <Icon
                                                iconKey={isMobileMenuOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                                                width={18}
                                                height={18}
                                                color={theme.foregroundMuted}
                                            />
                                        </div>
                                    </div>
                                </button>

                                {isMobileMenuOpen && (
                                    <>
                                        <div
                                            className="settings-modal-mobile-menu-backdrop"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        />

                                        <div
                                            className="settings-modal-mobile-menu-popup"
                                            style={{
                                                backgroundColor: theme.background,
                                                borderColor: theme.backgroundHover,
                                            }}
                                        >
                                            <div className="settings-modal-mobile-menu-popup-nav">
                                                {navigationContents}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {canSeeWorkspaceSettings && activeTab === TAB_NAME && (
                            <SettingsNamePane
                                theme={theme}
                                workspace={workspace}
                                canEditWorkspace={canEditWorkspace}
                            />
                        )}

                        {canSeeWorkspaceSettings && activeTab === TAB_PATH && (
                            <SettingsPathPane
                                theme={theme}
                                workspace={workspace}
                                canEditWorkspace={canEditWorkspace}
                            />
                        )}

                        {canSeeWorkspaceSettings && activeTab === TAB_PLAN && (
                            <SettingsPlanPane
                                theme={theme}
                                user={user}
                                workspace={workspace}
                                canEditWorkspace={canEditWorkspace}
                            />
                        )}

                        {canSeeWorkspaceSettings && activeTab === TAB_MEMBERS && (
                            <SettingsMembersPane
                                theme={theme}
                                subtitle={subtitle}
                                canInviteMembers={canInviteMembers}
                                onInviteClick={onInviteClick}
                                workspace={workspace}
                                errorMessage={errorMessage}
                                statusMessage={statusMessage}
                                sortedMembers={sortedMembers}
                                user={user}
                                canManageTarget={canManageTarget}
                                handleRoleChange={handleRoleChange}
                                handleRemoveMember={handleRemoveMember}
                            />
                        )}

                        {canSeeWorkspaceSettings && activeTab === TAB_PROFILES && (
                            <SettingsProfilesPane
                                theme={theme}
                                workspace={workspace}
                                user={user}
                            />
                        )}

                        {canSeeWorkspaceSettings && activeTab === TAB_DEFAULT_DISPLAY && (
                            <SettingsDisplayPane
                                theme={theme}
                                title="Default Display"
                                subtitle="Default appearance for this space"
                                settingLabel="Dark Mode"
                                settingDescription="Applied when members choose the space default."
                                isDarkMode={isWorkspaceDefaultDarkMode}
                                onThemeModeChange={handleWorkspaceDefaultThemeModeChange}
                            />
                        )}

                        {activeTab === TAB_ACCOUNT_DISPLAY && (
                            <SettingsAccountDisplayPane
                                theme={theme}
                                isDarkMode={isDarkMode}
                                onThemeModeChange={handleAccountThemeModeChange}
                                isUsingWorkspaceDefault={isUsingWorkspaceThemeDefault}
                            />
                        )}

                        {canSeeWorkspaceSettings && activeTab === TAB_ICON && (
                            <SettingsIconPane
                                theme={theme}
                                workspace={workspace}
                                canEditIcon={canEditWorkspace}
                            />
                        )}

                        {canSeeWorkspaceSettings && activeTab === TAB_COVER && (
                            <SettingsCoverPane
                                theme={theme}
                                workspace={workspace}
                                canEditCover={canEditWorkspace}
                            />
                        )}

                        {canSeeWorkspaceSettings && activeTab === TAB_INTEGRATIONS && (
                            <SettingsIntegrationsPane
                                theme={theme}
                                workspace={workspace}
                            />
                        )}

                        {canSeeWorkspaceSettings && activeTab === TAB_CHECKLISTS && (
                            <SettingsChecklistsPane
                                theme={theme}
                                workspace={workspace}
                                user={user}
                                canEditWorkspace={canEditWorkspace}
                            />
                        )}

                        {activeTab === TAB_PROFILE_PHOTO && (
                            <SettingsProfilePhotoPane
                                theme={theme}
                                user={user}
                            />
                        )}

                        {activeTab === TAB_USER_NAME && (
                            <SettingsUserNamePane
                                theme={theme}
                                user={user}
                            />
                        )}

                        {activeTab === TAB_LOGIN && (
                            <SettingsLoginPane
                                theme={theme}
                            />
                        )}

                        {/*
                        {activeTab === TAB_PAYOUTS && (
                            <div className="settings-modal-embedded-panel">
                                <StripeSettings />
                            </div>
                        )}
                        */}
                    </div>

                </div>
            </Modal>

            <Confirm
                title="Delete Account"
                message="Delete your account and all associated data? This action cannot be undone."
                isOpen={deleteAccountConfirmOpen}
                setOpen={setDeleteAccountConfirmOpen}
                onConfirm={handleConfirmDeleteAccount}
            />

            <Confirm
                title="Delete Space"
                message="Delete this workspace and all associated data? This action cannot be undone."
                isOpen={deleteWorkspaceConfirmOpen}
                setOpen={setDeleteWorkspaceConfirmOpen}
                onConfirm={handleConfirmDeleteWorkspace}
            />
        </>
    );

});

// Dev Tools
SettingsModal.displayName = 'SettingsModal';

export default SettingsModal;

